import React, { useState, useEffect } from "react";
import { Form, Input, InputNumber, Switch, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { showDanger } from "@/utils";
import { injectIntl } from "react-intl";

/**
 * 动态表单字段的 React 组件
 * @param {object} currentFunction - 包含字段元数据 (fieldsMeta) 和参数值 (params) 的对象
 * @param {function} handleParamChange - 处理字段值变化的函数 (currentFunction, key, value) => {}
 */
const DynamicFormFields = ({ currentFunction, handleParamChange, intl }) => {
    // 使用 React State 来管理 JSON 字段的文本缓存，类似于 Vue 代码中的 textCache
    const [textCache, setTextCache] = useState({});

    const flushJson = (field) => {
        const key = field.key;
        if (!key) {
            return;
        }
        const text = textCache[key] || "";
        try {
            const obj = JSON.parse(text);
            handleParamChange(currentFunction, key, obj);
        } catch {
            showDanger(
                `${currentFunction.name}${intl.formatMessage({ id: "gui.agentManage.field", defaultMessage: "'s " })}${key}${intl.formatMessage({ id: "gui.agentManage.fieldFormatError", defaultMessage: " field format error: Invalid JSON" })}`
            );
        }
    };

    // 使用 React Effect 在 currentFunction.params 变化时同步更新 JSON 字段的文本缓存
    // 这确保了当外部数据变化时，UI也能正确显示
    useEffect(() => {
        if (currentFunction && currentFunction.params) {
            const initialTextCache = {};
            currentFunction.fieldsMeta.forEach((field) => {
                if (
                    field.type === "json" &&
                    currentFunction.params[field.key]
                ) {
                    // 将 JS 对象格式化为可读的 JSON 字符串
                    initialTextCache[field.key] = JSON.stringify(
                        currentFunction.params[field.key],
                        null,
                        2
                    );
                }
            });
            setTextCache(initialTextCache);
        }
    }, [currentFunction]);

    // 根据字段类型渲染对应的 Ant Design 输入组件
    const renderInputComponent = (field) => {
        const fieldKey = field.key;
        const fieldValue = currentFunction.params[fieldKey];

        switch (field.type) {
            case "array":
                return (
                    <Input.TextArea
                        defaultValue={fieldValue}
                        onChange={(e) =>
                            handleParamChange(
                                currentFunction,
                                fieldKey,
                                e.target.value
                            )
                        }
                    />
                );

            case "json":
                return (
                    <Input.TextArea
                        rows={6}
                        placeholder={intl.formatMessage({ id: "gui.agentManage.enterValidJson", defaultMessage: "Please enter valid JSON" })}
                        defaultValue={textCache[fieldKey] || ""}
                        onChange={(e) =>
                            setTextCache((prev) => ({
                                ...prev,
                                [fieldKey]: e.target.value,
                            }))
                        }
                        onBlur={() => flushJson(field, textCache[fieldKey])} // 在失焦时调用 flushJson
                    />
                );

            case "number":
                return (
                    <InputNumber
                        style={{ width: "100%" }}
                        defaultValue={fieldValue}
                        onChange={(value) =>
                            handleParamChange(currentFunction, fieldKey, value)
                        }
                    />
                );

            case "boolean":
            case "bool":
                return (
                    <Switch
                        checked={fieldValue}
                        onChange={(checked) =>
                            handleParamChange(
                                currentFunction,
                                fieldKey,
                                checked
                            )
                        }
                    />
                );

            default: // 'string' 或其他类型
                return (
                    <Input
                        defaultValue={fieldValue}
                        onChange={(e) =>
                            handleParamChange(
                                currentFunction,
                                fieldKey,
                                e.target.value
                            )
                        }
                    />
                );
        }
    };

    const fieldRemark = (field) => {
        let description = field && field.label ? field.label : "";
        if (field.default) {
            description += intl.formatMessage(
                { id: "gui.agentManage.defaultValue", defaultMessage: " (Default: {value})" },
                { value: field.default }
            );
        }
        return description;
    };

    return (
        <>
            {currentFunction.fieldsMeta.map((field) => (
                <Form.Item
                    key={field.key}
                    // 自定义 label，包含标题和 Tooltip
                    label={
                        <>
                            <span
                                style={{
                                    fontSize: "16px",
                                    marginRight: "6px",
                                    display: "inline-block",
                                    width: "190px",
                                }}
                            >
                                {field.label}
                            </span>
                            <Tooltip title={fieldRemark(field)} placement="top">
                                {/* Ant Design 图标库是 React 生态中常用的选择 */}
                                <InfoCircleOutlined
                                    style={{ color: "#5778ff" }}
                                />
                            </Tooltip>
                        </>
                    }
                    // 动态添加 className
                    className={`param-item ${
                        field.type === "array" || field.type === "json"
                            ? "textarea-field"
                            : ""
                    }`}
                >
                    {renderInputComponent(field)}
                </Form.Item>
            ))}
        </>
    );
};

export default injectIntl(DynamicFormFields);